import { auth } from "auth"
import {
  combinePaths,
  getQueryParams,
  waitMillis,
  withQueryParams,
} from "utils"
import { DEFAULT_RESPONSE_TYPE } from "./constants"
import { FetchError } from "./FetchError"
import { RequestFailedError } from "./RequestFailedError"
import type { Headers, QueryParams } from "./types"

const { delay: delayQueryParam } = getQueryParams()

export type ResponseType = "text" | "json" | "blob"

export type RunFetchOptions<T, R extends ResponseType> = {
  path: string
  pathPrefix?: string
  baseUrl?: string
  method?: "GET" | "POST" | "DELETE" | "PATCH" | "PUT"
  headers?: Headers
  data?: unknown
  params?: QueryParams
  onError?: (error: FetchError) => any
  transform?: (response: DataFromResponseType<R>) => Promise<T> | T
  responseType?: R
  delay?: string | number
  signal?: AbortSignal
  /**
   * On a 401, `runFetch` redirects to the login service (`auth.refreshLogin`),
   * because for the platform API a 401 means the session is dead. Set this for
   * requests to *other* services (e.g. chat/Costanza) whose 401s must not bounce
   * the user out of the whole app — the caller handles the thrown `FetchError`.
   */
  skipLoginRedirect?: boolean
}

export const runFetch = async <
  T = unknown,
  R extends ResponseType = typeof DEFAULT_RESPONSE_TYPE,
>(
  options: RunFetchOptions<T, R>
): Promise<T> => {
  const {
    path,
    pathPrefix,
    baseUrl,
    data,
    params,
    onError,
    method = "GET",
    transform,
    delay = delayQueryParam,
    responseType = DEFAULT_RESPONSE_TYPE,
    signal,
    skipLoginRedirect = false,
  } = options

  if (delay) {
    await waitMillis(Number(delay))
  }

  let url = combinePaths(baseUrl, pathPrefix, path)
  if (params) url = withQueryParams(url, params)

  let status: number | undefined
  let text: string | undefined
  let json: unknown | undefined
  let blob: Blob | undefined

  let body: FormData | string | undefined
  if (data instanceof FormData) {
    body = data
  } else if (data) {
    body = JSON.stringify(data)
  } else {
    body = undefined
  }

  try {
    const req = new Request(url, {
      method,
      body,
      headers: await mergeHeaders(options),
      signal,
    })

    /**
     * iOS kills backgrounded apps and restores them when foregrounded,
     * but the network stack isn't immediately available. A single short
     * retry gives WebKit enough time to reconnect before we surface an error.
     */
    let response: Response
    try {
      response = await fetch(req.clone())
    } catch (error) {
      if (!isNetworkError(error)) throw error
      await waitMillis(300)
      response = await fetch(req)
    }
    status = response.status

    // If user needs to login again, we want to bounce them before any more logic is run.
    // This is to be expected to happen, it's not an error and should therefore not be logged to Sentry.
    // TODO - don't rethrow and log in Sentry. The try/catch'es of this function should be decoupled so we have clear control of this
    if (status === 401 && !skipLoginRedirect) {
      await auth.refreshLogin()
    }

    if (!response.ok) {
      try {
        text = await response.text()
        json = JSON.parse(text)
      } catch (e) {
        console.error(e)
      }
      throw new RequestFailedError()
    }

    if (!transform) return undefined as T

    if (responseType === "text") {
      text = await response.text()
    }
    if (responseType === "json") {
      const responseText = await response.text()
      if (responseText) {
        text = responseText
        json = JSON.parse(responseText)
      }
    }
    if (responseType === "blob") {
      blob = await response.blob()
    }

    const transformData: any = json ?? text ?? blob ?? null
    return await transform(transformData)
  } catch (error) {
    const resolved = resolveError({
      error,
      method,
      url,
      text,
      json,
      status,
    })
    await onError?.(resolved)
    throw resolved
  }
}

const mergeHeaders = async <T, R extends ResponseType>(
  args: RunFetchOptions<T, R>
) => {
  // Don't set Content-Type for FormData. Browser shoud handles it automatically
  const isFormData = args.data instanceof FormData
  const hasData = Boolean(args.data)
  const contentType =
    hasData && !isFormData ? { "Content-Type": "application/json" } : undefined

  return {
    ...contentType,
    ...args.headers,
  }
}

type ResolveErrorProps = {
  error: unknown
  method: string
  url: string
  status: number | undefined
  text: string | undefined
  json: unknown | undefined
}

const resolveError = ({
  error,
  status,
  url,
  method,
  json,
  text,
}: ResolveErrorProps): FetchError => {
  if (error instanceof FetchError) {
    return error
  }

  if (error instanceof RequestFailedError) {
    return new FetchError({
      url,
      method,
      status,
      text,
      json,
      message: undefined,
      cause: undefined,
    })
  }

  if (error instanceof Error) {
    return new FetchError({
      url,
      method,
      status,
      text,
      json,
      message: error.message,
      cause: error,
    })
  }

  throw new Error(`${error}`, { cause: error })
}

const isNetworkError = (error: unknown) => {
  if (!(error instanceof TypeError)) return false
  return error.message === "Failed to fetch" || error.message === "Load failed"
}

type DataFromResponseType<T> = T extends "text"
  ? string
  : T extends "json"
    ? unknown
    : T extends "blob"
      ? Blob
      : never
